import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import api from '../../../utils/api';
import useThemeStyles from '../../../theme/useThemeStyles';
import PlaceAutocompleteInput from '../PlaceAutocompleteInput';

function Step4Location({ formData, businessId, onComplete, onError }) {
  const styles = useThemeStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [serviceLocations, setServiceLocations] = useState([]);
  const [newPlaceInput, setNewPlaceInput] = useState('');

  useEffect(() => {
    if (formData.serviceLocations?.length > 0) {
      setServiceLocations(formData.serviceLocations);
    } else if (formData.location?.coordinates && formData.address) {
      const [lng, lat] = formData.location.coordinates;
      setServiceLocations([{
        address: formData.address,
        latitude: lat,
        longitude: lng,
        isPrimary: true
      }]);
    }
  }, [formData.serviceLocations, formData.location?.coordinates, formData.address]);

  const handleAddLocation = (place) => {
    if (!place?.address || place.latitude == null || place.longitude == null) return;
    const isPrimary = serviceLocations.length === 0;
    setServiceLocations(prev => [...prev, {
      address: place.address,
      latitude: place.latitude,
      longitude: place.longitude,
      isPrimary
    }]);
    setNewPlaceInput('');
  };

  const handleRemoveLocation = (index) => {
    setServiceLocations(prev => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length > 0 && next[0].isPrimary === false) {
        next[0] = { ...next[0], isPrimary: true };
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!businessId) {
      setError('Business ID is required. Please complete previous steps first.');
      return;
    }

    const primary = serviceLocations.find(l => l.isPrimary) || serviceLocations[0];
    const locationCoords = primary
      ? { type: 'Point', coordinates: [primary.longitude, primary.latitude] }
      : formData.location;

    try {
      setLoading(true);
      await api.put(`/api/supplier/business/${businessId}/location`, {
        location: locationCoords,
        serviceLocations: serviceLocations.map(({ address, latitude, longitude, isPrimary }) => ({
          address,
          latitude,
          longitude,
          isPrimary
        }))
      });

      onComplete({
        ...formData,
        location: locationCoords,
        serviceLocations
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update location';
      setError(errorMessage);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1, ...styles.text.primary }}>
        Where do you work your magic?
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, ...styles.text.secondary }}>
        Add the locations where you provide services. The first is your primary (main base); the rest are secondary areas you serve. Search and add as many as you need.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, ...styles.text.primary }}>
              Add locations
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <PlaceAutocompleteInput
                  value={newPlaceInput}
                  onChange={setNewPlaceInput}
                  onSelect={(place) => {
                    handleAddLocation(place);
                    setNewPlaceInput('');
                  }}
                  placeholder="Search for city, town or address (add multiple)"
                  label=""
                />
              </Box>
              <Button
                type="button"
                variant="outlined"
                disabled={!newPlaceInput.trim()}
                sx={{ mt: 0.5 }}
                onClick={async () => {
                  if (!newPlaceInput.trim()) return;
                  setError('');
                  const addresses = newPlaceInput.split(',').map(a => a.trim()).filter(Boolean);
                  if (addresses.length === 0) return;
                  try {
                    const results = await Promise.all(
                      addresses.map(addr =>
                        api.post('/api/geocode/address', { address: addr })
                          .then(res => res.data?.success && res.data?.data?.coordinates
                            ? { address: res.data.data.formattedAddress || addr, coordinates: res.data.data.coordinates }
                            : null
                          )
                          .catch(() => null)
                      )
                    );
                    const valid = results.filter(r => r && r.coordinates);
                    if (valid.length === 0) {
                      setError('Could not find those locations. Try selecting from the suggestions.');
                      return;
                    }
                    const newLocs = valid.map(({ address, coordinates }, _i) => {
                      const [lng, lat] = coordinates;
                      return { address, latitude: lat, longitude: lng, isPrimary: false };
                    });
                    newLocs[0].isPrimary = serviceLocations.length === 0;
                    setServiceLocations(prev => {
                      const combined = [...prev, ...newLocs];
                      if (combined.length > 0 && !combined[0].isPrimary) {
                        combined[0] = { ...combined[0], isPrimary: true };
                      }
                      return combined;
                    });
                    setNewPlaceInput('');
                  } catch {
                    setError('Could not find those locations. Try selecting from the suggestions.');
                  }
                }}
              >
                Add
              </Button>
            </Box>
          </Grid>

          {serviceLocations.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, ...styles.text.primary }}>
                Your locations
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {serviceLocations.map((loc, index) => (
                  <Chip
                    key={`${loc.address}-${index}`}
                    label={`${loc.isPrimary ? 'Primary' : 'Secondary'}: ${loc.address}`}
                    onDelete={() => handleRemoveLocation(index)}
                    color={loc.isPrimary ? 'primary' : 'default'}
                    variant={loc.isPrimary ? 'filled' : 'outlined'}
                    sx={{ maxWidth: '100%' }}
                  />
                ))}
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Continue'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

export default Step4Location;
